import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import UploadStatusDropdown from './UploadStatusDropdown';
import { fmDocumentService } from '../api/FMDocumentService';

type UploadStatus = 'pending' | 'processing' | 'success' | 'error';

interface FileUpload {
  file: File;
  taskId?: string;
  status: UploadStatus;
  error?: string;
}

interface UploadDocumentsProps {
  isDisabled?: boolean;
}

const UploadDocuments: React.FC<UploadDocumentsProps> = ({ isDisabled }) => {
  const { t } = useTranslation();
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('webkitdirectory', '');
      inputRef.current.setAttribute('directory', '');
    }
  }, []);

  const handleFolderSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    // Lista estensioni immagini
    const imageExtensions = new Set([
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.bmp',
      '.webp',
      '.tiff',
      '.svg'
    ]);

    // Filtra i file escludendo le immagini (sia per estensione che per MIME type)
    const files = Array.from(event.target.files).filter((file) => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return !imageExtensions.has(ext) && !file.type.startsWith('image/');
    });

    // inizializzo stato
    const initialUploads: FileUpload[] = files.map((file) => ({
      file,
      status: 'pending'
    }));
    setUploads(initialUploads);

    // avvio gli upload
    for (const file of files) {
      try {
        const { task_id } = await fmDocumentService.uploadSingleDocument(file);

        setUploads((prev) =>
          prev.map((u) => (u.file === file ? { ...u, taskId: task_id, status: 'processing' } : u))
        );

        // inizio polling
        pollTaskStatus(file, task_id);
      } catch (error: any) {
        setUploads((prev) =>
          prev.map((u) => (u.file === file ? { ...u, status: 'error', error: error.message } : u))
        );
      }
    }
  };

  // STEP 2: Polling stato Celery
  const pollTaskStatus = (file: File, taskId: string) => {
    const interval = setInterval(async () => {
      try {
        const { status, result } = await fmDocumentService.getUploadStatus(taskId);
        console.log(`Polling ${file.name}:`, status, result);
        if (status === 'SUCCESS') {
          clearInterval(interval);
          setUploads((prev) =>
            prev.map((u) => (u.file === file ? { ...u, status: 'success' } : u))
          );
        } else if (status === 'FAILURE') {
          clearInterval(interval);
          setUploads((prev) =>
            prev.map((u) =>
              u.file === file ? { ...u, status: 'error', error: result?.error || 'Errore' } : u
            )
          );
        }
        // se PENDING o STARTED → continuo a pollare
      } catch (err: any) {
        clearInterval(interval);
        setUploads((prev) =>
          prev.map((u) => (u.file === file ? { ...u, status: 'error', error: err.message } : u))
        );
      }
    }, 15000);
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
      <Tooltip title={t('entities.document.multi-folder-upload')} arrow placement="top">
        <span>
          <Button
            onClick={() => inputRef.current?.click()}
            color="primary"
            variant="contained"
            disabled={isDisabled}
            sx={{
              height: 40,
              width: 40,
              minWidth: 40,
              p: 0,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}>
            <FolderCopyIcon fontSize="small" />
          </Button>
        </span>
      </Tooltip>

      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFolderSelect}
      />

      <UploadStatusDropdown uploads={uploads} />
    </Box>
  );
};

export default UploadDocuments;
